import { useCallback, useState } from 'react'

import { Input } from 'antd'
import { faAt, faCity, faEnvelope, faGlobe, faIdBadge, faIdCard, faLocationArrow, faSave, faStethoscope } from '@fortawesome/pro-solid-svg-icons'
import { useSelector } from 'react-redux'

import FormItem from '../../../Components/shared/Forms/FormItem'
import CountriesSelector from '../../../Components/shared/Selectors/CountriesSelector'
import { Properties } from '../../../utils/entities'
import { getErrors, getFormError, getIdOrNull, handleStateSelectId, validateString } from '../../../utils'
import IconButton from '../../../Components/antd/Buttons/IconButton'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { Institution, INSTITUTION_VALIDATORS } from '../../../utils/entities/institution'
import { isValid } from '../../../utils/validators'

const { ACRONYM, ADDRESS, CITY, COUNTRY, EMAIL, NAME, SECTOR, ZIPCODE } = Properties
const VALIDATORS = INSTITUTION_VALIDATORS([ACRONYM, ADDRESS, CITY, COUNTRY, NAME, ZIPCODE])

const StudentInstitutionForm = ({ data, onSubmit }) => {
  const t = useSelector(selectTranslate)

  const [formData, setFormData] = useState({ ...data })
  const [error, setError] = useState({ missingFields: [] })

  const handleSubmit = useCallback(() => {
    const institution = new Institution(formData)

    const errors = isValid(institution, VALIDATORS)

    if (!validateString(formData.email, { notEmpty: true, isEmail: true }).valid) {
      errors.keys.push(EMAIL)
      errors.messages[EMAIL] = ['NOT_VALID_EMAIL']
    }

    if (!validateString(formData.sector, { notEmpty: true }).valid) {
      errors.keys.push(SECTOR)
      errors.messages[SECTOR] = ['NOT_VALID_SECTOR']
    }

    if (errors.keys.length === 0) {
      onSubmit({ ...institution, email: formData.email, sector: formData.sector })
    } else {
      const details = {}

      Object.keys(errors.messages).forEach(key => {
        details[t(key)] = errors.messages[key].map(m => t(m))
      })

      setError(error => ({
        ...error,
        title: t('form.errors'),
        details,
        visible: true,
        missingFields: errors.keys
      }))
    }
  }, [t, formData, onSubmit])

  const errors = getErrors(error)

  return (
    <>
      <div className='h-100 w-100 flex-column'>
        <h3 className='pdl-12'> {t('common.institution')}</h3>
        <div className='pd-12'>
          <FormItem icon={faIdCard} label='common.name.dd' required>
            <Input
              className={getFormError(errors[NAME])}
              value={formData[NAME]}
              onChange={e => setFormData({ ...formData, [NAME]: e.target.value })}
            />
          </FormItem>
          <FormItem icon={faIdBadge} label='common.acronym.dd' required>
            <Input
              className={getFormError(errors[ACRONYM])}
              value={formData.acronym ?? null}
              onChange={e => setFormData({ ...formData, [ACRONYM]: e.target.value })}
            />
          </FormItem>
          <FormItem icon={faLocationArrow} label='common.address.dd' required>
            <Input
              className={getFormError(errors[ADDRESS])}
              value={formData.address ?? null}
              onChange={e => setFormData({ ...formData, [ADDRESS]: e.target.value })}
            />
          </FormItem>
          <FormItem icon={faCity} label='common.city.dd' required>
            <Input
              className={getFormError(errors[CITY])}
              value={formData.city ?? null}
              onChange={e => setFormData({ ...formData, [CITY]: e.target.value })}
            />
          </FormItem>
          <FormItem icon={faEnvelope} label='common.zipcode.dd' required>
            <Input
              className={getFormError(errors[ZIPCODE])}
              value={formData.zipcode ?? null}
              onChange={e => setFormData({ ...formData, [ZIPCODE]: e.target.value })}
            />
          </FormItem>
          <FormItem icon={faGlobe} label='common.country.dd' className={`${getFormError(errors[COUNTRY])} form-select`} required>
            <CountriesSelector
              className={getFormError(errors[COUNTRY])}
              values={getIdOrNull(formData[COUNTRY])}
              onChange={handleStateSelectId(setFormData, COUNTRY)}
            />
          </FormItem>
          <FormItem icon={faAt} label='common.email.dd' required>
            <Input
              className={getFormError(errors[EMAIL])}
              value={formData.email ?? null}
              onChange={e => setFormData({ ...formData, [EMAIL]: e.target.value })}
            />
          </FormItem>
          <FormItem icon={faStethoscope} label='common.sector.dd' required>
            <Input
              className={getFormError(errors[SECTOR])}
              value={formData.sector ?? null}
              onChange={e => setFormData({ ...formData, [SECTOR]: e.target.value })}
            />
          </FormItem>
        </div>
      </div>
      <div className='flex-row w-100 j-ctn-e'>
        <IconButton type='primary' icon={faSave} onClick={handleSubmit} text={t('actions.save')} />
      </div>
    </>
  )
}

export default StudentInstitutionForm
